import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import { useAuth, formatApiError } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from;
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const u = await login(email, password);
            toast.success(`Welcome back, ${u.name}`);
            const home = u.role === "admin" ? "/admin" : u.role === "broker" ? "/broker" : "/member";
            navigate(from || home, { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    const quickFill = (a) => {
        setEmail(a.email);
        setPassword(a.password);
    };

    return (
        <AuthShell
            title="Welcome back to the flock."
            subtitle="Sign in to your Perx portal."
            side="virtual"
        >
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div>
                    <Label
                        htmlFor="email"
                        className="text-navy-700 text-xs uppercase tracking-widest"
                    >
                        Email
                    </Label>
                    <Input
                        id="email"
                        type="email"
                        required
                        data-testid="login-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                    />
                </div>
                <div>
                    <Label
                        htmlFor="password"
                        className="text-navy-700 text-xs uppercase tracking-widest"
                    >
                        Password
                    </Label>
                    <Input
                        id="password"
                        type="password"
                        required
                        data-testid="login-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                    />
                    <div className="mt-2 text-right">
                        <Link
                            to="/forgot-password"
                            data-testid="login-forgot-link"
                            className="text-xs text-navy-500 hover:text-gold-600 font-medium"
                        >
                            Forgot password?
                        </Link>
                    </div>
                </div>

                {err && (
                    <div
                        data-testid="login-error"
                        className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    >
                        {err}
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={busy}
                    data-testid="login-submit"
                    className="w-full h-12 rounded-full bg-navy-700 hover:bg-navy-800 text-white font-semibold"
                >
                    {busy ? "Signing in…" : "Sign in"}
                </Button>

                <div className="text-sm text-navy-500 text-center">
                    New to Perx?{" "}
                    <Link
                        to="/signup"
                        data-testid="login-signup-link"
                        className="text-navy-700 font-semibold hover:text-gold-600"
                    >
                        Join the flock
                    </Link>
                </div>
            </form>
        </AuthShell>
    );
}
